export interface SentrySdkConfig {
  enabled: boolean;
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate: number;
  debug: boolean;
  sendDefaultPii: boolean;
  autoSessionTracking: boolean;
  prismaIntegrationEnabled: boolean;
  includeGraphqlVariables: boolean;
}

type ConfigSource = Record<
  string,
  string | number | boolean | null | undefined
>;

export const DEFAULT_SENTRY_SDK_CONFIG: SentrySdkConfig = {
  enabled: false,
  tracesSampleRate: 0,
  debug: false,
  sendDefaultPii: false,
  autoSessionTracking: false,
  prismaIntegrationEnabled: false,
  includeGraphqlVariables: false,
};

export function parseBoolean(value: unknown, defaultValue = false): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return defaultValue;

  const normalized = value.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;

  return defaultValue;
}

export function parseSampleRate(value: unknown, defaultValue = 0): number {
  let parsed = Number.NaN;
  if (typeof value === "number") {
    parsed = value;
  } else if (typeof value === "string") {
    parsed = Number.parseFloat(value);
  }

  if (!Number.isFinite(parsed)) return defaultValue;
  return Math.min(1, Math.max(0, parsed));
}

export function parseSentrySdkConfig(
  source: ConfigSource,
  options: { publicPrefix?: string } = {},
): SentrySdkConfig {
  const prefix = options.publicPrefix ?? "";
  const get = (key: string) => source[`${prefix}${key}`];
  const dsn = stringOrUndefined(get("SENTRY_DSN"));
  const enabled = parseBoolean(get("SENTRY_ENABLED"), false) && Boolean(dsn);

  return {
    enabled,
    dsn,
    environment: stringOrUndefined(get("SENTRY_ENVIRONMENT")),
    release: stringOrUndefined(get("SENTRY_RELEASE")),
    tracesSampleRate: enabled
      ? parseSampleRate(get("SENTRY_TRACES_SAMPLE_RATE"), 0)
      : 0,
    debug: enabled ? parseBoolean(get("SENTRY_DEBUG"), false) : false,
    sendDefaultPii: enabled
      ? parseBoolean(get("SENTRY_SEND_DEFAULT_PII"), false)
      : false,
    autoSessionTracking: enabled
      ? parseBoolean(get("SENTRY_AUTO_SESSION_TRACKING"), false)
      : false,
    prismaIntegrationEnabled: enabled
      ? parseBoolean(get("SENTRY_PRISMA_INTEGRATION_ENABLED"), false)
      : false,
    includeGraphqlVariables: enabled
      ? parseBoolean(get("SENTRY_INCLUDE_GRAPHQL_VARIABLES"), false)
      : false,
  };
}

function stringOrUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
